import React, {useEffect, useState, useRef} from "react";
import {getTranslations} from "../services/language.service.js";

export const Select = (props) => {
    const LANG = getTranslations();
    const select = useRef(null);
    const listRef = useRef(null);
    const label = props.label ?? LANG.pleaseSelect;
    const options = props.options ?? [];
    const [value, setValue] = useState(props.value ?? undefined);
    const [openOptions, setOpenOptions] = useState(false);
    const [focusedOptionIndex, setFocusedOptionIndex] = useState(-1);
    const id = props.id || `select-${Math.random().toString(36).substring(2, 9)}`;

    useEffect(() => {
        const handleClick = (e) => {
            for (let element = e.target; element !== null; element = element.parentNode) {
                if (element === select.current) {
                    return;
                }
            }

            setOpenOptions(false);
        };

        document.addEventListener('click', handleClick);
        return () => document.removeEventListener("click", handleClick);
    }, []);

    const onChange = (e) => {
        if (e.target.value === '') {
            reset(e);
            return;
        }

        setValue(e.target.value);

        if (props.hasOwnProperty('onSelect')) {
            props.onSelect(e.target.value);
        }
    };

    const getLabel = () => {
        if (value === undefined) {
            return label;
        }

        return value;
    };

    useEffect(() => {
        setValue(props.value ?? undefined);
    }, [props.value]);

    useEffect(() => {
        // Wenn die Options-Liste geöffnet wird, setze den Fokus auf die aktuell ausgewählte Option
        if (openOptions && listRef.current) {
            if (value) {
                const selectedIndex = options.indexOf(value);
                setFocusedOptionIndex(selectedIndex !== -1 ? selectedIndex + 1 : 0); // +1 wegen der "Bitte wählen" Option
            } else {
                setFocusedOptionIndex(0);
            }
        }
    }, [openOptions, value, options]);

    const reset = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setValue(undefined);

        if (props.hasOwnProperty('onSelect')) {
            props.onSelect(undefined);
        }
    };

    const toggleOpenOptions = () => {
        setOpenOptions(!openOptions);
    };

    const selectOption = (option) => {
        setValue(option);

        if (props.hasOwnProperty('onSelect')) {
            props.onSelect(option);
        }

        setOpenOptions(false);
    };

    const handleKeyDown = (e) => {
        switch (e.key) {
            case 'Enter':
            case ' ':
                e.preventDefault();
                if (openOptions && focusedOptionIndex !== -1) {
                    // Wenn die Liste offen ist und eine Option fokussiert ist, wähle diese Option
                    const selectedOption = focusedOptionIndex === 0 ? undefined : options[focusedOptionIndex - 1];
                    selectOption(selectedOption);
                } else {
                    // Ansonsten toggle die Liste
                    toggleOpenOptions();
                }
                break;
            case 'Escape':
                e.preventDefault();
                setOpenOptions(false);
                break;
            case 'ArrowDown':
                e.preventDefault();
                if (!openOptions) {
                    toggleOpenOptions();
                } else {
                    setFocusedOptionIndex((prevIndex) =>
                        prevIndex < options.length ? prevIndex + 1 : 0
                    );
                }
                break;
            case 'ArrowUp':
                e.preventDefault();
                if (!openOptions) {
                    toggleOpenOptions();
                } else {
                    setFocusedOptionIndex((prevIndex) =>
                        prevIndex > 0 ? prevIndex - 1 : options.length
                    );
                }
                break;
            case 'Tab':
                if (openOptions) {
                    setOpenOptions(false);
                }
                break;
            default:
                // Bei Eingabe eines Buchstabens zur ersten passenden Option springen
                if (e.key.length === 1 && openOptions) {
                    const char = e.key.toLowerCase();
                    const matchingIndex = options.findIndex(option =>
                        option.toLowerCase().startsWith(char)
                    );

                    if (matchingIndex !== -1) {
                        setFocusedOptionIndex(matchingIndex + 1); // +1 wegen "Bitte wählen"
                    }
                }
                break;
        }
    };

    return (
        <div
            className={`select ${value !== undefined ? 'selected' : ''}`}
            ref={select}
            tabIndex="0"
            role="combobox"
            aria-expanded={openOptions}
            aria-haspopup="listbox"
            aria-label={props.ariaLabel || "Auswahl"}
            aria-controls={`${id}-listbox`}
            onClick={toggleOpenOptions}
            onKeyDown={handleKeyDown}
            aria-activedescendant={focusedOptionIndex !== -1 ? `${id}-option-${focusedOptionIndex}` : undefined}
        >
            <select onChange={onChange} tabIndex="-1" aria-hidden="true">
                <option value="">{LANG.pleaseSelect}</option>
                {options.map(option => <option key={option} value={option}>{option}</option>)}
            </select>
            <div className="select-label">
                {LANG["filter_"+getLabel().replaceAll(/[\s.]/g, '')] ?? getLabel()}
            </div>
            <div className="select-toggle" aria-hidden="true">
            </div>
            <ul
                id={`${id}-listbox`}
                className={`select-options ${openOptions ? 'open' : ''}`}
                role="listbox"
                ref={listRef}
                aria-label={props.ariaLabel || "Auswahl"}
            >
                <li
                    role="option"
                    id={`${id}-option-0`}
                    aria-selected={value === undefined}
                    className={focusedOptionIndex === 0 ? 'focused' : ''}
                    onClick={() => selectOption(undefined)}
                >
                    {LANG.pleaseSelect}
                </li>
                {options.sort((a, b) => a.localeCompare(b, 'en', {'sensitivity': 'base'})).map((option, index) => (
                    <li
                        key={option}
                        role="option"
                        id={`${id}-option-${index + 1}`}
                        aria-selected={value === option}
                        className={focusedOptionIndex === index + 1 ? 'focused' : ''}
                        onClick={() => selectOption(option)}
                    >
                        {LANG["filter_"+option.replaceAll(/[\s.]/g, '')] ?? option}
                    </li>
                ))}
            </ul>
        </div>
    );
};
